import { updatePrivacyPolicyStatus } from '../services/privacyPolicyService';

/**
 * Sync cookie consent preferences with Cognito user attributes
 * @param {string} userId - The Cognito user ID
 * @param {Object} cookiePreferences - Cookie preferences from CookieConsent
 * @returns {Promise<void>}
 */
export const syncCookieConsentWithCognito = async (userId, cookiePreferences) => {
  if (!userId || !cookiePreferences) {
    return;
  }

  try {
    const analyticsAccepted = cookiePreferences.categories?.analytics?.wanted || false;
    const preferencesAccepted = cookiePreferences.categories?.preferences?.wanted || false;

    await updatePrivacyPolicyStatus(userId, true, analyticsAccepted, preferencesAccepted);
  } catch (error) {
    // Silently fail in production
    if (process.env.NODE_ENV === 'development') {
      console.error('Error syncing cookie consent with Cognito:', error);
    }
  }
};

/**
 * Get current cookie consent state from CookieConsent library
 * @returns {Object|null} Cookie consent state or null if not available
 */
export const getCurrentCookieConsent = () => {
  if (typeof (window as any).CookieConsent === 'undefined') {
    return null;
  }

  return {
    categories: (window as any).CookieConsent.config.categories,
    version: (window as any).CookieConsent.config.version,
  };
};

/**
 * Setup listener for cookie consent changes
 * @param {string} userId - The Cognito user ID
 * @param {Function} callback - Optional callback to execute after sync
 */
export const setupCookieConsentListener = (userId, callback?: (prefs) => void) => {
  if (!userId) {
    return;
  }

  // Listen for clicks on consent buttons
  document.addEventListener('click', (event: MouseEvent) => {
    const target = event.target;

    if (!(target instanceof HTMLElement)) return;

    // Check if user clicked on any consent-related button
    if (
      target.classList.contains('consent-give') ||
      target.classList.contains('ccm__footer') ||
      target.textContent?.includes('Accept all cookies') ||
      target.textContent?.includes('Save current settings')
    ) {
      setTimeout(async () => {
        const cookiePreferences = getCurrentCookieConsent();
        if (cookiePreferences) {
          await syncCookieConsentWithCognito(userId, cookiePreferences);
          callback?.(cookiePreferences);
        }
      }, 300);
    }
  });
};
